/**
 * SEO Component
 * Centralized SEO management with Helmet
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { siteConfig, PageMeta, BreadcrumbItem } from '../data/seo';
import {
  generateOpenGraphMeta,
  generateTwitterMeta,
  generateBreadcrumbSchema,
} from '../utils/seo';

interface SEOProps {
  meta: PageMeta;
  schema?: object | object[];
  breadcrumbs?: BreadcrumbItem[];
  children?: React.ReactNode;
}

const SEO: React.FC<SEOProps> = ({ meta, schema, breadcrumbs, children }) => {
  const ogMeta = generateOpenGraphMeta(meta);
  const twitterMeta = generateTwitterMeta(meta);

  // Combine schemas
  const allSchemas: object[] = [];
  if (schema) {
    if (Array.isArray(schema)) {
      allSchemas.push(...schema);
    } else {
      allSchemas.push(schema);
    }
  }
  if (breadcrumbs && breadcrumbs.length > 0) {
    allSchemas.push(generateBreadcrumbSchema(breadcrumbs));
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={meta.canonical} />
      
      {/* Keywords (if provided) */}
      {meta.keywords && meta.keywords.length > 0 && (
        <meta name="keywords" content={meta.keywords.join(', ')} />
      )}
      
      {/* Robots */}
      {meta.noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}
      
      {/* Open Graph */}
      <meta property="og:type" content={ogMeta['og:type']} />
      <meta property="og:title" content={ogMeta['og:title']} />
      <meta property="og:description" content={ogMeta['og:description']} />
      <meta property="og:url" content={ogMeta['og:url']} />
      <meta property="og:image" content={ogMeta['og:image']} />
      <meta property="og:site_name" content={ogMeta['og:site_name']} />
      <meta property="og:locale" content={ogMeta['og:locale']} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterMeta['twitter:card']} />
      <meta name="twitter:site" content={twitterMeta['twitter:site']} />
      <meta name="twitter:title" content={twitterMeta['twitter:title']} />
      <meta name="twitter:description" content={twitterMeta['twitter:description']} />
      <meta name="twitter:image" content={twitterMeta['twitter:image']} />
      
      {/* Geo Tags */}
      <meta name="geo.region" content="US-FL" />
      <meta name="geo.placename" content={`${siteConfig.address.city}, ${siteConfig.address.state}`} />
      
      {/* Schema.org JSON-LD */}
      {allSchemas.map((s, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
      
      {children}
    </Helmet>
  );
};

export default SEO;



